import { ErrorLabel, ErrorText, ErrorTextTheme, Field } from '@stories/Elements/Forms/Fields/Form.styles';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { TextboxTheme } from '../Textbox/Textbox';
import { LabelUnderline } from '../Textbox/Textbox.styles';
import { SelectUnderlineStyles as s } from './Select.styles';

export enum SelectTheme {
  White = 0,
  Black = 1,
  Red = 2,
  Red_Base = 3,
  White_Text = 4,
}

export interface SelectProps {
  disabled?: boolean;
  displayError: boolean;
  isRequired?: boolean;
  labelActive?: boolean;
  labelText?: string;
  labelTheme?: TextboxTheme;
  onChange: (value: string | undefined) => void;
  showPlaceholder?: boolean;
  theme?: SelectTheme;
  errorTextTheme?: ErrorTextTheme;
  title?: string;
  titleErrorMessage?: string;
  titleOptions: KeyValuePair<string, string>[];
  value: string;
  hideMarginBottom?: boolean;
}

const SelectUnderline: FunctionComponent<SelectProps> = ({
  disabled,
  displayError,
  isRequired,
  labelActive,
  labelText,
  labelTheme,
  onChange,
  showPlaceholder,
  theme,
  errorTextTheme,
  title,
  titleErrorMessage,
  titleOptions,
  value,
  hideMarginBottom = false,
}) => {
  const [placeholderColour, setPlaceholderColour] = useState<boolean>(theme === SelectTheme.Black);
  const selectRef = useRef<HTMLDivElement>(null);
  const items = showPlaceholder === true ? titleOptions : titleOptions.slice(1);

  if (!titleOptions || titleOptions.length === 0) {
    return null;
  }

  let valueToShow = titleOptions.find((x) => x.key === value);

  if (valueToShow === undefined) {
    valueToShow = titleOptions.find((x) => x.key === 'unspecified');
  }

  const [dropdownActive, setDropdownActive] = useState<boolean>(false);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function handleClickOutside(event: any): void {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setDropdownActive(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectRef]);

  const handleClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
    if (e.currentTarget === null) {
      return;
    }
    onChange(e.currentTarget.dataset.key);
    setPlaceholderColour(false);
  };

  return (
    <Field data-no-margin={hideMarginBottom}>
      {title && title !== undefined ? (
        <s.Title theme={theme}>{title}</s.Title>
      ) : (
        <LabelUnderline isActive={labelActive ?? value !== ''} theme={labelTheme}>
          {labelText && labelText.length > 0 ? labelText : titleOptions[0].value}
          {isRequired ? '*' : null}
        </LabelUnderline>
      )}
      <s.SelectUnderlineContainer
        ref={selectRef}
        activeState={dropdownActive}
        disabled={disabled}
        placeholderColour={placeholderColour}
        selectTheme={theme}
        onClick={() => {
          if (disabled) {
            return;
          }
          setDropdownActive(!dropdownActive);
        }}
      >
        <s.ActiveSelect>{valueToShow ? valueToShow.value : titleOptions[0].value}</s.ActiveSelect>
        <s.SelectUnderlineDropdown activeState={dropdownActive} theme={theme}>
          {/* Need to slice the placeholder value */}
          {items.map((x) => (
            <s.SelectUnderlineItem key={x.key} data-key={x.key} data-value={x.value} onClick={(e) => handleClick(e)}>
              {x.value}
            </s.SelectUnderlineItem>
          ))}
        </s.SelectUnderlineDropdown>
      </s.SelectUnderlineContainer>
      {displayError && (
        <ErrorLabel aria-label="form-error">
          <ErrorText theme={errorTextTheme ? errorTextTheme : theme === SelectTheme.Black ? 'black' : 'white'}>
            {titleErrorMessage}
          </ErrorText>
        </ErrorLabel>
      )}
    </Field>
  );
};

export default SelectUnderline;
